/**
 * Demo login credential derivation.
 *
 * These functions mirror the EPS source of truth exactly — copy any changes
 * made there into this file and update the pinned tests.
 *
 * EPS sources:
 *   - apps/enrollment-platform-service/src/modules/customer/demo/demo-company.constants.ts
 *   - apps/enrollment-platform-service/src/modules/demo/services/demo-company-token.ts
 *   - apps/enrollment-platform-service/src/modules/demo/services/demo-company-display-name.ts
 *   - apps/enrollment-platform-service/src/modules/demo/services/demo-credentials.ts
 *   - apps/enrollment-platform-service/src/modules/demo/services/demo-login-provisioning.service.ts
 *
 * Pass the stored company name (e.g. "ZZ - DEMO - Acme Health, Inc."). The
 * demo prefix is stripped here, so the stripped display name also works.
 */

/** Mirrors EPS DEMO_NAME_PREFIX_PATTERN — keep in exact sync. */
const DEMO_NAME_PREFIX_PATTERN = /^\s*zz\s*-?\s*demo\s*-?\s*/i
const DEMO_TOKEN_MAX_LENGTH = 14
const DEMO_DISPLAY_NAME_MAX_LENGTH = 50

const DEMO_EMAIL_DOMAIN = 'surecolabs.com'
const HR_ADMIN_LAST_NAME = 'HRAdmin'
const EMPLOYEE_LAST_NAME = 'Employee'
const HR_ADMIN_EMAIL_SUFFIX = '-a'
const EMPLOYEE_EMAIL_SUFFIX = '-ee'

/** Mirrors EPS demo-company-token.ts — keep in exact sync. */
export function tokenizeCompanyName(name: string): string {
  const stripped = name.replace(DEMO_NAME_PREFIX_PATTERN, '').trim()
  return stripped
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, DEMO_TOKEN_MAX_LENGTH)
    .replace(/-+$/, '')
}

/** Mirrors EPS demo-company-display-name.ts — keep in exact sync. */
export function buildDemoCompanyDisplayName(name: string): string {
  return name
    .replace(DEMO_NAME_PREFIX_PATTERN, '')
    .trim()
    .slice(0, DEMO_DISPLAY_NAME_MAX_LENGTH)
}

/** Mirrors EPS demo-credentials.ts — keep in exact sync. */
export function buildDemoPassword(token: string): string {
  return `Demo1!${token}`
}

export interface DemoLoginEntry {
  /** Display name as provisioned (e.g. "Acme Health, Inc. HRAdmin"). */
  name: string
  /** Cognito login email. */
  email: string
  /** Deterministic password — valid until the user changes it. */
  password: string
}

export interface DemoLoginsResult {
  hrAdmin: DemoLoginEntry
  employee: DemoLoginEntry
}

/**
 * Re-derives both demo login entries from the stored company name. Returns
 * null when the name (or its token) is blank so the caller can render an
 * unavailable state without computing stale values.
 *
 * Mirrors EPS demo-login-provisioning.service.ts (provisionDemoLogins /
 * provisionHrAdmin / provisionEmployee) — keep in exact sync.
 */
export function deriveDemoLogins(
  storedCompanyName: string | null | undefined
): DemoLoginsResult | null {
  if (!storedCompanyName?.trim()) return null

  const token = tokenizeCompanyName(storedCompanyName)
  if (!token) return null

  const displayName = buildDemoCompanyDisplayName(storedCompanyName)
  const password = buildDemoPassword(token)

  return {
    hrAdmin: {
      name: `${displayName} ${HR_ADMIN_LAST_NAME}`,
      email: `${token}${HR_ADMIN_EMAIL_SUFFIX}@${DEMO_EMAIL_DOMAIN}`,
      password,
    },
    employee: {
      // Lowercase -ee suffix is required: the legacy /user/login Lambda
      // lowercases the submitted username before Cognito sign-in, so an
      // uppercase suffix fails.
      name: `${displayName} ${EMPLOYEE_LAST_NAME}`,
      email: `${token}${EMPLOYEE_EMAIL_SUFFIX}@${DEMO_EMAIL_DOMAIN}`,
      password,
    },
  }
}
